import React from 'react';
import AppButton from '@common/components/app-button';
import {ModalIds} from '@common/models/enums/modal-ids.enum';
import useModalWithData from '@common/hooks/use-modal-with-data';
import {ReactComponent as LogoutIcon} from '@assets/icons/logout.svg';
import {animated} from "@react-spring/web";
import {useModalAnimation} from "../../features/core/animations";
import AppModal from "../../features/core/components/app-modal";

export interface LogoutConfirmModalData {
    onConfirm?: () => void | Promise<void>;
}

const LogoutConfirmModal: React.FC = () => {
    const {hideModal, data, isOpenModal} = useModalWithData<LogoutConfirmModalData>(ModalIds.LOGOUT_CONFIRM);
    const {springProps, closeModal} = useModalAnimation(!!isOpenModal(), 'fast', 'zoom-out');

    const modalData = data as LogoutConfirmModalData | null;
    if (!modalData) return null;

    const {onConfirm} = modalData;

    const handleConfirm = async () => {
        try {
            await onConfirm?.();
            hideModal();
        } catch (error) {
            console.error('Błąd podczas wylogowywania:', error);
        }
    };

    const handleCancel = () => {
        closeModal(() => hideModal());
    };

    return (
        <AppModal
            id={ModalIds.LOGOUT_CONFIRM}
            key={ModalIds.LOGOUT_CONFIRM}
            zIndex={50}
            forceFixed={true}>
            <animated.div
                style={springProps}
                className="h-full w-full flex flex-col bg-surface-level-1 rounded-2xl border border-outline-mild shadow-lvl-2 py-4 px-6 max-w-md"
            >
                <div className="flex items-center gap-3 mb-4">
                    <div
                        className="flex-shrink-0 w-10 h-10 bg-on-surface/5 rounded-full flex items-center justify-center">
                        <LogoutIcon className="w-5 h-5 text-on-surface"/>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-on-surface">
                            Wylogować się?
                        </h3>
                        <p className="text-sm text-on-surface/70">
                            Zostaniesz wylogowany z panelu
                        </p>
                    </div>
                </div>

                <div className="mb-6">
                    <p className="text-on-surface/80 text-sm leading-relaxed">
                        Czy na pewno chcesz się wylogować? Aby wrócić do panelu, będziesz musiał zalogować się ponownie.
                    </p>
                </div>

                <div className="flex gap-3 flex-wrap md:flex-nowrap">
                    <AppButton
                        onClick={handleCancel}
                        className="w-full md:w-fit flex-1 font-semibold border-2 border-on-surface hover:bg-on-surface/5"
                    >
                        Anuluj
                    </AppButton>
                    <AppButton
                        onClick={handleConfirm}
                        className="w-full md:w-fit flex-1 bg-primary text-on-primary font-semibold hover:bg-primary/80"
                    >
                        Wyloguj
                    </AppButton>
                </div>
            </animated.div>
        </AppModal>
    );
};

export default LogoutConfirmModal;
